import axios from "axios";
import axiosRetry from "axios-retry";
import { attachAuthInterceptors } from "auth/setupApiClient";
import appConfig from "configs/app.config";
import { invokeOmsService } from "./apiService";
import { buildQueryParams } from "../utils/helper";

const onBoardingUrl = {
  2: appConfig.v2.createBulkRecordTypes,
  3: appConfig.v2.createBulkRequestTypes,
  4: appConfig.v2.createBulkServices,
  5: appConfig.v2.createBulkServiceMapping,
  6: appConfig.v2.createBulkBodyParts,
};

let attachmentClient = null;
const getAttachmentClient = () => {
  if (attachmentClient) return attachmentClient;
  attachmentClient = axios.create({
    baseURL: appConfig.api.domain,
    timeout: appConfig.api.timeout,
    withCredentials: true,
    responseType: "blob",
  });
  attachAuthInterceptors(attachmentClient);
  axiosRetry(attachmentClient, { retries: appConfig.api.retry });
  return attachmentClient;
};

const medicalReqistration = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.medicalReqistration,
    body,
  };
  return await invokeOmsService(requestParams);
};

const lawOfficeRegistration = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.lawOfficeRegistration,
    body,
  };
  return await invokeOmsService(requestParams);
};

const backOfficeRegistration = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.backOfficeRegistration,
    body,
  };
  return await invokeOmsService(requestParams);
};

const insuranceCompanyRegistration = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.insuranceCompanyRegistration,
    body,
  };
  return await invokeOmsService(requestParams);
};

const professionalRegistration = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.professionalRegistration,
    body,
  };
  return await invokeOmsService(requestParams);
};

const patientRegistration = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.patientRegistration,
    body,
  };
  return await invokeOmsService(requestParams);
};

const accountDetails = async (body, id) => {
  const requestParams = {
    method: "PUT",
    url: appConfig.v2.accountDetails.replace("{id}", id),
    body,
  };
  return await invokeOmsService(requestParams);
};
const login = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.login,
    body,
  };
  return await invokeOmsService(requestParams);
};

const resendEmailVerification = async () => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.resendEmailVerification,
  };
  return await invokeOmsService(requestParams);
};

const onBoardingGeneralSettings = (body) => {
  const requestParams = {
    method: "PUT",
    url: appConfig.v2.updatePrefixes,
    body,
  };
  return invokeOmsService(requestParams);
};
const onBoardingDataSettings = (step, body) => {
  let payload = body;

  // createBulkServices requires `name` (not description)
  if (
    onBoardingUrl[step] === appConfig.v2.createBulkServices &&
    Array.isArray(body)
  ) {
    payload = body.map((item) => {
      const { description, ...rest } = item;
      return {
        ...rest,
        name: (item.name || item.description || "").trim(),
      };
    });
  }

  const requestParams = {
    method: "POST",
    url: onBoardingUrl[step],
    body: payload,
  };
  return invokeOmsService(requestParams);
};

const onBoardingPaymentSettings = (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.onBoardingPaymentSetting,
    body,
  };
  return invokeOmsService(requestParams);
};

const loadDefaults = async () => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.loadDefaults,
    body: {},
  };
  return await invokeOmsService(requestParams);
};
const forgotPassword = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.forgotPassword,
    body,
  };
  return await invokeOmsService(requestParams);
};

const resetPassword = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.resetPassword,
    body,
  };
  return invokeOmsService(requestParams);
};
const generateAuthenticatorQR = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.generateAuthenticatorQR,
    body,
  };
  return await invokeOmsService(requestParams);
};

const setUpAuthenticatorDetails = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.setUpAuthenticator,
    body,
  };
  return await invokeOmsService(requestParams);
};

const verifiedLogin = async () => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.verifiedLogin,
    body: {},
  };
  return await invokeOmsService(requestParams);
};

const updateDomain = async (body) => {
  const requestParams = {
    method: "PUT",
    url: appConfig.v2.updateAuthDomain,
    body,
  };
  return await invokeOmsService(requestParams);
};

const verifyLoginPassword = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.verifyLoginPassword,
    body,
  };
  return await invokeOmsService(requestParams);
};

const refreshSession = async () => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.refreshSession,
    body: {},
  };
  return await invokeOmsService(requestParams);
};

const signOut = async () => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.signout,
    body: {},
  };
  return await invokeOmsService(requestParams);
};

const validateAuthenticator = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.validateAuthenticator,
    body,
  };
  return await invokeOmsService(requestParams);
};

const otpValidate = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.otpValidate,
  };
  return await invokeOmsService(requestParams);
};

const verify2faRecoveryCode = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.verify2faRecoveryCode,
    body,
  };
  return await invokeOmsService(requestParams);
};

const createMedicalFacility = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.createMedicalFacility,
    body,
  };
  return await invokeOmsService(requestParams);
};

const verifyToken = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.verifyToken,
    body,
  };
  return await invokeOmsService(requestParams);
};
const disable2FA = async (id) => {
  const requestParams = {
    method: "PATCH",
    url: appConfig.v2.disable2FA.replace("{id}", id),
  };
  return await invokeOmsService(requestParams);
};

const reset2FA = async (id) => {
  const requestParams = {
    method: "STRICT_DELETE",
    url: appConfig.v2.reset2FA.replace("{id}", id),
  };
  return invokeOmsService(requestParams);
};

const enable2FA = async (id) => {
  const requestParams = {
    method: "PATCH",
    url: appConfig.v2.enable2FA.replace("{id}", id),
  };
  return invokeOmsService(requestParams);
};

const createLawOfficeFacility = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.createLawOffice,
    body,
  };
  return await invokeOmsService(requestParams);
};

const getLawOfficeFacility = async (body) => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.getLawOffice,
    body,
  };
  return await invokeOmsService(requestParams);
};

const verifyOtp = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.verifyOtp,
    body,
  };
  return await invokeOmsService(requestParams);
};

const resendOtp = async () => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.resendOTP,
  };
  return await invokeOmsService(requestParams);
};

const getBranchbyAccountIdLawOffice = async (payload) => {
  const queryString = buildQueryParams(payload, {
    pageNumber: 1,
    perPage: 10,
    sortBy: "createdAt",
    orderBy: "asc",
  });

  const requestParams = {
    method: "GET",
    url: appConfig.v2.getLawOffice + `?${queryString}`,
  };
  return await invokeOmsService(requestParams);
};
const getBranchbyAccountIdMedicalFacility = async (payload) => {
  const queryString = buildQueryParams(payload, {
    pageNumber: 1,
    perPage: 10,
    sortBy: "createdAt",
    orderBy: "asc",
  });

  const requestParams = {
    method: "GET",
    url: appConfig.v2.getMedicalFacility + `?${queryString}`,
  };

  return await invokeOmsService(requestParams);
};

const getAccountUsers = async (payload) => {

 const queryString = buildQueryParams(payload, {
    pageNumber : 1,
    perPage  : 10,
    sortBy : "name",
    orderBy : "asc",
  }) 
  const requestParams = {
    method: "GET",
    url:
      appConfig.v2.accountUsers +
      `?${queryString}`,
  };
  return await invokeOmsService(requestParams);
};

const getAccountUsersDetails = async (id) => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.accountUserDetails.replace("{id}", id),
  };
  return await invokeOmsService(requestParams);
};

const getAccountById = async (id) => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.getAccountById.replace("{id}", id),
  };
  return await invokeOmsService(requestParams);
};

const getEarningDetailsById = async (id) => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.getEarningDetailsById.replace("{id}", id),
  };
  return await invokeOmsService(requestParams);
};

const updateAccountInfo = async (id, body) => {
  const requestParams = {
    method: "PUT",
    url: appConfig.v2.updateAccountInfo.replace("{id}", id),
    body,
  };
  return await invokeOmsService(requestParams);
};

const getSettingAccountById = async (id) => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.getSettingAccountById.replace("{id}", id),
  };
  return await invokeOmsService(requestParams);
};

// createEmployee
const getAccountUserRoles = async () => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.accountRoles,
  };
  return await invokeOmsService(requestParams);
};

const createEmployee = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.createEmployee,
    body,
  };
  return await invokeOmsService(requestParams);
};

const updateEmployee = async (id, body) => {
  const requestParams = {
    method: "PUT",
    url: appConfig.v2.updateEmployee.replace("{id}", id),
    body,
  };
  return await invokeOmsService(requestParams);
};

const updateGeneral = async (body) => {
  const requestParams = {
    method: "PUT",
    url: appConfig.v2.updateGeneral,
    body,
  };
  return await invokeOmsService(requestParams);
};

const makeIsOwner = async (body) => {
  const requestParams = {
    method: "PATCH",
    url: appConfig.v2.transferUserOwner,
    body,
  };
  return await invokeOmsService(requestParams);
};

const requestOwnershipTransferOtp = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.ownershipTransferRequestOtp,
    body,
  };
  return await invokeOmsService(requestParams);
};

const resendOwnershipTransferOtp = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.ownershipTransferResendOtp,
    body,
  };
  return await invokeOmsService(requestParams);
};

const verifyOwnershipTransferOtp = async (body) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.ownershipTransferVerifyOtp,
    body,
  };
  return await invokeOmsService(requestParams);
};

const updateEmployeeStatus = async (id, bodyData) => {
  const requestParams = {
    method: "PUT",
    url: appConfig.v2.updateEmployeeStatus.replace("{id}", id),
    body: bodyData,
  };
  return await invokeOmsService(requestParams);
};
const getSupportMessages = async (orderId) => {
  const requestParams = {
    method: "GET",
    url: appConfig.v2.getSupportMessages.replace("{orderId}", orderId),
  };
  return await invokeOmsService(requestParams);
};

const postSupportMessages = async (body, onUploadProgress) => {
  const requestParams = {
    method: "POST",
    url: appConfig.v2.postSupportMessages,
    body,
    onUploadProgress,
  };
  return await invokeOmsService(requestParams);
};

const getSupportAttachment = async (id, index, disposition = "inline") => {
  const url =
    appConfig.v2.getSupportAttachmentUrl
      .replace("{id}", id)
      .replace("{index}", index) + `?disposition=${disposition}`;

  return getAttachmentClient().get(url);
};

const putByIdSupportMessages = async (id, body) => {
  const requestParams = {
    method: "PUT",
    url: appConfig.v2.putByIdSupportMessages.replace("{id}", id),
    body,
  };
  return await invokeOmsService(requestParams);
};

const AuthService = {
  getSupportMessages,
  postSupportMessages,
  getSupportAttachment,
  putByIdSupportMessages,
  updateEmployeeStatus,
  medicalReqistration,
  lawOfficeRegistration,
  backOfficeRegistration,
  insuranceCompanyRegistration,
  professionalRegistration,
  patientRegistration,
  accountDetails,
  login,
  resendEmailVerification,
  onBoardingDataSettings,
  onBoardingPaymentSettings,
  loadDefaults,
  otpValidate,
  forgotPassword,
  resetPassword,
  generateAuthenticatorQR,
  setUpAuthenticatorDetails,
  verifiedLogin,
  updateDomain,
  verifyLoginPassword,
  refreshSession,
  signOut,
  validateAuthenticator,
  verify2faRecoveryCode,
  getAccountById,
  getEarningDetailsById,
  updateAccountInfo,
  getSettingAccountById,
  createMedicalFacility,
  createLawOfficeFacility,
  getLawOfficeFacility,
  getBranchbyAccountIdMedicalFacility,
  getBranchbyAccountIdLawOffice,
  disable2FA,
  reset2FA,
  enable2FA,
  verifyToken,
  verifyOtp,
  resendOtp,
  getAccountUsers,
  getAccountUsersDetails,
  getAccountUserRoles,
  createEmployee,
  updateEmployee,
  makeIsOwner,
  requestOwnershipTransferOtp,
  resendOwnershipTransferOtp,
  verifyOwnershipTransferOtp,
  onBoardingGeneralSettings,
  updateGeneral,
};
export default AuthService;